/**
 * Gate 4 · Slice 4.5 · Creative Image Studio (UI)
 *
 * Renders the image generation + composition + Visual Guardian UI within an
 * asset_group. Isolated in its own file to keep CreativeStudioPanel from
 * growing unbounded. UI never surfaces provider names — only Standard /
 * Premium / Auto tier labels.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { VoiceGuidesPanel } from "./VoiceGuidesPanel";

const TIER_OPTIONS = [
  { id: "standard", label: "Standard", note: "faster · lower cost" },
  { id: "premium", label: "Premium", note: "stronger fidelity · in-image text" },
  { id: "auto", label: "Auto", note: "picked from prompt" },
];

function formatApiError(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (d.reason) return d.reason;
  try { return JSON.stringify(d); } catch { return String(d); }
}

function shortHash(h) {
  if (!h || typeof h !== "string") return "—";
  return h.slice(0, 8);
}

// -------------------------------------------------------------------------
// Public component
// -------------------------------------------------------------------------
export function ImageStudioBlock({ assetGroupId, groupIndex, onCostRefresh, backendUrl, brandId, onImageVersionsChange }) {
  const [templates, setTemplates] = useState({});
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // Form state
  const [tier, setTier] = useState("standard");
  const [templateId, setTemplateId] = useState("square_ig");
  const [variantCount, setVariantCount] = useState(2);
  const [altText, setAltText] = useState("");
  const [prompt, setPrompt] = useState("");
  const [pollJobId, setPollJobId] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [tplR, vR] = await Promise.all([
        api.get(`/creative/image-templates`).catch(() => ({ data: { templates: {} } })),
        api.get(`/creative/asset-groups/${assetGroupId}/image-versions`).catch(
          () => ({ data: { image_versions: [] } }),
        ),
      ]);
      setTemplates(tplR.data?.templates || {});
      setVersions(vR.data?.image_versions || []);
      onImageVersionsChange?.(vR.data?.image_versions || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [assetGroupId]);

  // Poll a running image_generate job until it completes.
  useEffect(() => {
    if (!pollJobId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get(`/intelligence-jobs/${pollJobId}`);
        const st = r.data?.status;
        if (st === "completed") {
          if (!cancelled) {
            setBusy(false);
            setPollJobId(null);
            toast.success("Images ready");
            await reload();
            onCostRefresh?.();
          }
          return;
        }
        if (st === "failed") {
          if (!cancelled) {
            setBusy(false);
            setPollJobId(null);
            toast.error("Image gen failed", {
              description: r.data?.error?.code || "provider_error",
            });
          }
          return;
        }
      } catch {}
      if (!cancelled) setTimeout(tick, 1200);
    };
    tick();
    return () => { cancelled = true; };
  }, [pollJobId]);

  const submitGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Prompt required");
      return;
    }
    if (!altText.trim() || altText.trim().length < 12) {
      toast.error("Alt text required (min 12 chars)");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(
        `/creative/asset-groups/${assetGroupId}/generate/image`,
        {
          prompt: prompt.trim(),
          tier,
          template: templateId,
          variant_count: variantCount,
          alt_text: altText.trim(),
        },
      );
      toast.success("Image job queued", {
        description: `Job ${shortHash(r.data.job_id)} · tier ${tier}`,
      });
      setPollJobId(r.data.job_id);
    } catch (e) {
      setBusy(false);
      toast.error("Image gen refused", { description: formatApiError(e) });
    }
  };

  const templateList = useMemo(
    () => Object.values(templates || {}),
    [templates],
  );

  return (
    <div
      data-testid={`camp-creative-group-${groupIndex}-image-studio`}
      style={{
        marginTop: 14,
        border: "1px solid var(--bo-line)",
        borderRadius: 6,
        background: "var(--bo-panel)",
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="bo-btn"
        data-testid={`camp-creative-group-${groupIndex}-image-toggle`}
        style={{
          width: "100%", textAlign: "left", padding: "8px 12px",
          fontSize: 12.5, background: "transparent", border: "none",
          borderBottom: expanded ? "1px solid var(--bo-line)" : "none",
          fontFamily: "var(--bo-mono)",
          color: "var(--bo-fg)",
          cursor: "pointer",
        }}
      >
        {expanded ? "▾" : "▸"}  IMAGE STUDIO  ·  {versions.length} version
        {versions.length === 1 ? "" : "s"}
      </button>
      {expanded && (
        <div style={{ padding: 12, display: "grid", gap: 14 }}>
          {/* Slice 4.6 · Advisory context tuning (voice guides) */}
          <VoiceGuidesPanel groupIndex={groupIndex} brandId={brandId} />
          {/* Generator form */}
          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {TIER_OPTIONS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTier(t.id)}
                  data-testid={`camp-creative-group-${groupIndex}-image-tier-${t.id}`}
                  aria-pressed={tier === t.id}
                  className="bo-btn"
                  style={{
                    fontSize: 11,
                    padding: "4px 10px",
                    background: tier === t.id
                      ? "var(--bo-accent, #b98a4a)"
                      : "transparent",
                    color: tier === t.id ? "#fff" : "var(--bo-fg)",
                    border: "1px solid var(--bo-line)",
                    borderRadius: 4,
                  }}
                  title={t.note}
                >
                  {t.label}
                  <span style={{ marginLeft: 6, fontSize: 10, opacity: 0.75 }}>
                    · {t.note}
                  </span>
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {templateList.map((tpl) => (
                <button
                  key={tpl.id}
                  type="button"
                  onClick={() => setTemplateId(tpl.id)}
                  data-testid={`camp-creative-group-${groupIndex}-image-tpl-${tpl.id}`}
                  aria-pressed={templateId === tpl.id}
                  className="bo-btn"
                  style={{
                    fontSize: 11,
                    padding: "3px 8px",
                    background: templateId === tpl.id
                      ? "var(--bo-panel-alt)"
                      : "transparent",
                    border: templateId === tpl.id
                      ? "1px solid var(--bo-fg)"
                      : "1px solid var(--bo-line)",
                    borderRadius: 4,
                    fontFamily: "var(--bo-mono)",
                  }}
                >
                  {tpl.aspect_ratio}
                  <span style={{ marginLeft: 6, opacity: 0.7 }}>
                    {tpl.label}
                  </span>
                </button>
              ))}
            </div>
            <label
              className="bo-eyebrow"
              style={{ fontSize: 10, marginBottom: -4 }}
            >
              Prompt
            </label>
            <textarea
              data-testid={`camp-creative-group-${groupIndex}-image-prompt`}
              rows={3}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe the image scene. Include mood, subjects, and any composition intent."
              disabled={busy}
              style={{
                fontSize: 12.5,
                padding: 8,
                border: "1px solid var(--bo-line)",
                borderRadius: 4,
                fontFamily: "inherit",
                background: "var(--bo-panel-alt)",
                color: "var(--bo-fg)",
              }}
              maxLength={2000}
            />
            <label
              className="bo-eyebrow"
              style={{ fontSize: 10, marginBottom: -4 }}
            >
              Alt text (accessibility · Guardian rule)
            </label>
            <input
              type="text"
              data-testid={`camp-creative-group-${groupIndex}-image-alt`}
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              placeholder="Describe the image for someone who cannot see it (min 12 chars, avoid 'image of')"
              disabled={busy}
              maxLength={280}
              style={{
                fontSize: 12,
                padding: "6px 8px",
                border: "1px solid var(--bo-line)",
                borderRadius: 4,
                background: "var(--bo-panel-alt)",
                color: "var(--bo-fg)",
              }}
            />
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <label
                className="bo-eyebrow"
                style={{ fontSize: 10, minWidth: 80 }}
              >
                Variants
              </label>
              <input
                type="number"
                min={1}
                max={4}
                value={variantCount}
                onChange={(e) => setVariantCount(
                  Math.max(1, Math.min(4, Number(e.target.value) || 1)),
                )}
                data-testid={`camp-creative-group-${groupIndex}-image-count`}
                disabled={busy}
                style={{
                  width: 60,
                  fontSize: 12,
                  padding: "4px 6px",
                  border: "1px solid var(--bo-line)",
                  borderRadius: 4,
                  background: "var(--bo-panel-alt)",
                  color: "var(--bo-fg)",
                }}
              />
              <button
                type="button"
                onClick={submitGenerate}
                disabled={busy}
                className="bo-btn bo-btn-primary"
                data-testid={`camp-creative-group-${groupIndex}-image-generate-btn`}
                style={{ marginLeft: "auto", fontSize: 12, padding: "5px 14px" }}
              >
                {busy ? "Generating…" : `Generate ${variantCount} image${variantCount === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>

          {/* Existing image versions */}
          {loading && <div style={{ fontSize: 11, color: "var(--bo-muted)" }}>Loading…</div>}
          {versions.map((iv) => (
            <ImageVersionCard
              key={iv.id}
              iv={iv}
              groupIndex={groupIndex}
              backendUrl={backendUrl}
              onChanged={reload}
              onCostRefresh={onCostRefresh}
            />
          ))}
          {!loading && versions.length === 0 && (
            <div style={{ fontSize: 11, color: "var(--bo-muted)" }}>
              No image versions yet. Generate the first batch above.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------
// Image version card
// -------------------------------------------------------------------------
function ImageVersionCard({ iv, groupIndex, backendUrl, onChanged, onCostRefresh }) {
  const [reguardianBusy, setReguardianBusy] = useState(false);
  const [advisoryBusy, setAdvisoryBusy] = useState(false);

  const doReguardian = async () => {
    setReguardianBusy(true);
    try {
      await api.post(
        `/creative/image-versions/${iv.id}/guardian/re-evaluate`,
      );
      toast.success("Visual Guardian re-evaluated");
      await onChanged();
    } catch (e) {
      toast.error("Guardian re-eval failed", { description: formatApiError(e) });
    } finally {
      setReguardianBusy(false);
    }
  };

  const doAdvisory = async () => {
    setAdvisoryBusy(true);
    try {
      const r = await api.post(
        `/creative/image-versions/${iv.id}/guardian/advisory`,
        {},
      );
      toast.success("Visual advisory queued", {
        description: `Job ${shortHash(r.data.job_id)} · cost-visible · non-blocking`,
      });
      onCostRefresh?.();
    } catch (e) {
      toast.error("Advisory failed", { description: formatApiError(e) });
    } finally {
      setAdvisoryBusy(false);
    }
  };

  return (
    <div
      data-testid={`camp-creative-group-${groupIndex}-image-version-${iv.id}`}
      style={{
        border: "1px solid var(--bo-line)",
        borderRadius: 6,
        padding: 10,
        background: "var(--bo-panel-alt)",
      }}
    >
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span
          style={{
            fontSize: 10,
            padding: "2px 8px",
            borderRadius: 3,
            background: iv.tier_resolved === "premium"
              ? "var(--bo-accent, #b98a4a)"
              : "var(--bo-panel)",
            color: iv.tier_resolved === "premium" ? "#fff" : "var(--bo-fg)",
            border: "1px solid var(--bo-line)",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            fontFamily: "var(--bo-mono)",
          }}
        >
          {iv.tier_resolved === "premium" ? "Premium" : "Standard"}
          {iv.tier_requested === "auto" && (
            <span style={{ marginLeft: 4, opacity: 0.75 }}>· via Auto</span>
          )}
        </span>
        <span style={{ fontSize: 11, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)" }}>
          {iv.aspect_ratio} · {iv.template}
        </span>
        <span style={{ fontSize: 11, color: "var(--bo-muted)" }}>
          {iv.variants?.length || 0} variant
          {(iv.variants?.length || 0) === 1 ? "" : "s"}
        </span>
        <span
          style={{
            fontSize: 11,
            color: "var(--bo-muted)",
            fontFamily: "var(--bo-mono)",
          }}
        >
          ${Number(iv.cost_usd || 0).toFixed(4)}
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <button
            type="button"
            onClick={doReguardian}
            disabled={reguardianBusy}
            className="bo-btn"
            data-testid={`camp-creative-group-${groupIndex}-image-guardian-reeval-btn`}
            style={{ fontSize: 11, padding: "3px 8px" }}
            title="Re-run Visual Guardian deterministic pass"
          >
            {reguardianBusy ? "…" : "Re-Guardian"}
          </button>
          <button
            type="button"
            onClick={doAdvisory}
            disabled={advisoryBusy}
            className="bo-btn"
            data-testid={`camp-creative-group-${groupIndex}-image-guardian-advisory-btn`}
            style={{ fontSize: 11, padding: "3px 8px" }}
            title="Queue non-blocking multimodal advisory"
          >
            {advisoryBusy ? "…" : "Advisory"}
          </button>
        </div>
      </div>

      <div
        style={{
          marginTop: 10,
          display: "grid",
          gap: 10,
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        }}
      >
        {(iv.variants || []).map((v, vi) => (
          <ImageVariantCard
            key={v.id}
            variant={v}
            imageVersionId={iv.id}
            groupIndex={groupIndex}
            index={vi}
            backendUrl={backendUrl}
            onChanged={onChanged}
          />
        ))}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Image variant card
// -------------------------------------------------------------------------
function ImageVariantCard({ variant, imageVersionId, groupIndex, index, backendUrl, onChanged }) {
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeHeadline, setComposeHeadline] = useState(
    variant.branded_headline || variant.composition_headline || "",
  );
  const [composeCta, setComposeCta] = useState(
    variant.branded_cta || variant.composition_cta || "",
  );
  const [composing, setComposing] = useState(false);
  const [showComposed, setShowComposed] = useState(
    Boolean(variant.branded_preview_key || variant.composition_preview_key),
  );
  const [useBranded, setUseBranded] = useState(true);

  const submitCompose = async () => {
    if (!composeHeadline.trim()) {
      toast.error("Headline required");
      return;
    }
    setComposing(true);
    try {
      const url = useBranded
        ? `/creative/image-versions/${imageVersionId}/variants/${variant.id}/branded-compose`
        : `/creative/image-versions/${imageVersionId}/variants/${variant.id}/compose`;
      const r = await api.post(url, {
        headline: composeHeadline.trim(),
        cta: composeCta.trim() || null,
      });
      const trail = r.data?.composition?.precedence_trail;
      toast.success(useBranded ? "Branded preview rendered" : "Thin preview rendered", {
        description: trail
          ? `Precedence: ${trail.map((t) => t.layer).slice(0, 3).join(" → ")}${trail.length > 3 ? "…" : ""}`
          : undefined,
      });
      setShowComposed(true);
      setComposeOpen(false);
      await onChanged();
    } catch (e) {
      toast.error("Compose failed", { description: formatApiError(e) });
    } finally {
      setComposing(false);
    }
  };

  const brandedActive = Boolean(variant.branded_preview_key);
  const thinActive = Boolean(variant.composition_preview_key);
  const composedUrlSuffix = showComposed && brandedActive
    ? "/branded"
    : showComposed && thinActive
      ? "?composed=1"
      : "";
  const imgUrl = `${backendUrl}/api/brand-os/creative/images/${imageVersionId}/${variant.id}${composedUrlSuffix}`;
  const guardian = variant.guardian_latest;
  const counts = guardian?.counts || { block: 0, warn: 0, info: 0 };
  const blockN = counts.block || 0;
  const warnN = counts.warn || 0;
  const infoN = counts.info || 0;
  const tone = blockN > 0 ? "danger" : warnN > 0 ? "warn" : "muted";

  return (
    <div
      data-testid={`camp-creative-group-${groupIndex}-image-variant-${index}`}
      style={{
        border: "1px solid var(--bo-line)",
        borderRadius: 6,
        background: "var(--bo-panel)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "relative",
          aspectRatio: variant.aspect_ratio?.replace(":", " / ") || "1 / 1",
          background: "#111",
        }}
      >
        <img
          src={imgUrl}
          alt={variant.alt_text || "generated image"}
          data-testid={`camp-creative-group-${groupIndex}-image-variant-${index}-img`}
          style={{
            width: "100%", height: "100%", objectFit: "cover",
            display: "block",
          }}
          onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
        />
        {showComposed && (brandedActive || thinActive) && (
          <span
            style={{
              position: "absolute", top: 6, left: 6,
              fontSize: 9, padding: "2px 6px",
              background: brandedActive
                ? "rgba(185, 138, 74, 0.85)"
                : "rgba(0,0,0,0.6)",
              color: "#fff",
              borderRadius: 3, letterSpacing: 0.5,
              textTransform: "uppercase",
              fontFamily: "var(--bo-mono)",
            }}
          >
            {brandedActive ? "branded" : "composed"}
          </span>
        )}
      </div>
      <div style={{ padding: 8, display: "grid", gap: 6 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <span
            data-testid={`camp-creative-group-${groupIndex}-image-variant-${index}-guardian-pill`}
            style={{
              fontSize: 10, padding: "2px 7px", borderRadius: 3,
              fontFamily: "var(--bo-mono)",
              background: tone === "danger"
                ? "rgba(179, 74, 74, 0.12)"
                : tone === "warn"
                  ? "rgba(200, 148, 51, 0.14)"
                  : "var(--bo-panel-alt)",
              color: tone === "danger"
                ? "var(--bo-danger, #b34a4a)"
                : tone === "warn"
                  ? "var(--bo-warn, #b8892a)"
                  : "var(--bo-muted)",
              border: "1px solid var(--bo-line)",
            }}
            title={guardian
              ? `Visual Guardian rule set ${guardian.rule_set_version}`
              : "Not evaluated yet"}
          >
            Guardian:
            {blockN > 0 ? ` ${blockN} BLOCK` : ""}
            {warnN > 0 ? ` · ${warnN} WARN` : ""}
            {infoN > 0 && blockN === 0 && warnN === 0 ? ` ${infoN} INFO` : ""}
            {blockN === 0 && warnN === 0 && infoN === 0 ? " clean" : ""}
          </span>
          {(brandedActive || thinActive) && (
            <button
              type="button"
              onClick={() => setShowComposed((v) => !v)}
              className="bo-btn"
              data-testid={`camp-creative-group-${groupIndex}-image-variant-${index}-toggle-composed`}
              style={{ fontSize: 10, padding: "1px 6px" }}
            >
              {showComposed ? "raw" : brandedActive ? "branded" : "composed"}
            </button>
          )}
          <button
            type="button"
            onClick={() => setComposeOpen((v) => !v)}
            className="bo-btn"
            data-testid={`camp-creative-group-${groupIndex}-image-variant-${index}-compose-btn`}
            style={{ marginLeft: "auto", fontSize: 10, padding: "1px 6px" }}
            title="Slice 4.5 · thin composition (Slice 4.6 adds branded rules)"
          >
            {composeOpen ? "Cancel" : "Compose"}
          </button>
        </div>
        {guardian?.findings?.length > 0 && (
          <details style={{ fontSize: 10.5, color: "var(--bo-muted)" }}>
            <summary style={{ cursor: "pointer" }}>
              {guardian.findings.length} finding
              {guardian.findings.length === 1 ? "" : "s"}
            </summary>
            <ul style={{ margin: "4px 0 0 12px", paddingLeft: 4 }}>
              {guardian.findings.map((f, i) => (
                <li key={i}>
                  <span
                    style={{
                      fontFamily: "var(--bo-mono)",
                      fontSize: 9,
                      padding: "0 4px", borderRadius: 2, marginRight: 4,
                      background: f.severity === "block"
                        ? "rgba(179, 74, 74, 0.15)"
                        : f.severity === "warn"
                          ? "rgba(200, 148, 51, 0.15)"
                          : "var(--bo-panel-alt)",
                    }}
                  >
                    {String(f.severity || "").toUpperCase()}
                  </span>
                  <span>{f.rule}: {f.message}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
        {variant.guardian_advisory_latest && Array.isArray(variant.guardian_advisory_latest.notes) && (
          <details style={{ fontSize: 10, color: "var(--bo-muted)" }}>
            <summary style={{ cursor: "pointer" }}>
              Advisory notes ({variant.guardian_advisory_latest.notes.length}) · non-blocking
            </summary>
            <ul style={{ margin: "4px 0 0 12px", paddingLeft: 4 }}>
              {variant.guardian_advisory_latest.notes.map((n, i) => (
                <li key={i}>
                  <em>[{n.tag}]</em> {n.message}
                </li>
              ))}
            </ul>
          </details>
        )}
        {composeOpen && (
          <div
            style={{
              marginTop: 4,
              padding: 6,
              background: "var(--bo-panel-alt)",
              border: "1px dashed var(--bo-line)",
              borderRadius: 4,
              display: "grid",
              gap: 4,
            }}
          >
            <div style={{ display: "flex", gap: 4 }}>
              <button
                type="button"
                onClick={() => setUseBranded(true)}
                aria-pressed={useBranded}
                data-testid={`camp-creative-group-${groupIndex}-image-variant-${index}-compose-mode-branded`}
                className="bo-btn"
                style={{
                  fontSize: 10, padding: "2px 8px",
                  background: useBranded ? "var(--bo-accent, #b98a4a)" : "transparent",
                  color: useBranded ? "#fff" : "var(--bo-fg)",
                }}
                title="Applies §9 truth precedence: governance → org truth → … → creative instructions"
              >
                Branded (§9)
              </button>
              <button
                type="button"
                onClick={() => setUseBranded(false)}
                aria-pressed={!useBranded}
                data-testid={`camp-creative-group-${groupIndex}-image-variant-${index}-compose-mode-thin`}
                className="bo-btn"
                style={{
                  fontSize: 10, padding: "2px 8px",
                  background: !useBranded ? "var(--bo-panel)" : "transparent",
                }}
              >
                Thin preview
              </button>
            </div>
            <input
              type="text"
              value={composeHeadline}
              onChange={(e) => setComposeHeadline(e.target.value)}
              maxLength={60}
              placeholder="Headline (≤ 60 chars)"
              data-testid={`camp-creative-group-${groupIndex}-image-variant-${index}-compose-headline`}
              style={{
                fontSize: 11.5, padding: "4px 6px",
                border: "1px solid var(--bo-line)",
                borderRadius: 3,
                background: "var(--bo-panel)",
                color: "var(--bo-fg)",
              }}
            />
            <input
              type="text"
              value={composeCta}
              onChange={(e) => setComposeCta(e.target.value)}
              maxLength={30}
              placeholder="CTA (optional · ≤ 30 chars)"
              data-testid={`camp-creative-group-${groupIndex}-image-variant-${index}-compose-cta`}
              style={{
                fontSize: 11.5, padding: "4px 6px",
                border: "1px solid var(--bo-line)",
                borderRadius: 3,
                background: "var(--bo-panel)",
                color: "var(--bo-fg)",
              }}
            />
            <button
              type="button"
              onClick={submitCompose}
              disabled={composing}
              className="bo-btn bo-btn-primary"
              data-testid={`camp-creative-group-${groupIndex}-image-variant-${index}-compose-submit`}
              style={{ fontSize: 11, padding: "3px 8px" }}
            >
              {composing ? "…" : "Render preview"}
            </button>
          </div>
        )}
        {variant.alt_text && (
          <div style={{ fontSize: 10, color: "var(--bo-muted)" }}>
            <span
              className="bo-eyebrow"
              style={{ marginRight: 4, fontSize: 9 }}
            >
              alt
            </span>
            {variant.alt_text}
          </div>
        )}
      </div>
    </div>
  );
}
